import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnnouncer } from './useAnnouncer'
import { assembleWav, generateRecordingFilename } from '@/utils/audioAssembler'
import {
  getChunks,
  getSession,
  updateSessionStatus,
  deleteSession,
  type RecordingSession,
} from '@/services/RecordingDatabase'
import { isTranscriptStreaming } from '@/services/LiveStreamingTranscripts'
import { buildTranscriptionLink } from '@/utils/transcriptionLink'
import { initApiService } from '@/services/init'
import {
  queryUploadedBytes as queryResumableBytes,
  toUploadProgress,
  uploadFromOffset as uploadResumableFromOffset,
  ResumableUploadError,
  type UploadProgress,
} from '@/utils/resumableUpload'
import { extractApiErrorMessage, UserFacingError, logUnexpectedFailure } from '@/utils/errorHandling'

export type RecordingUploadStatus =
  | 'idle'
  | 'assembling'
  | 'initiating'
  | 'uploading'
  | 'completed'
  | 'failed'

export type { UploadProgress }

export interface UseRecordingUploadOptions {
  /** Callback when upload completes successfully */
  onSuccess?: (transcriptId: string) => void
  /** Callback when upload fails */
  onError?: (error: string) => void
  /** Callback for progress updates */
  onProgress?: (progress: UploadProgress) => void
}

export interface UseRecordingUploadReturn {
  // State
  status: Ref<RecordingUploadStatus>
  progress: Ref<UploadProgress>
  error: Ref<string | null>
  transcriptId: Ref<string | null>

  // Computed
  isUploading: ComputedRef<boolean>
  canRetry: ComputedRef<boolean>

  // Actions
  upload: (session: RecordingSession) => Promise<boolean>
  retry: () => Promise<boolean>
  cancel: () => void
  reset: () => void
}

const DEFAULT_MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000

/**
 * Composable for uploading a completed recording session to the transcription service.
 *
 * This handles the full upload flow:
 * 1. Assembles PCM chunks from IndexedDB into WAV format
 * 2. Initiates upload via whisper service API
 * 3. Uploads to GCS using resumable upload protocol
 * 4. Cleans up IndexedDB on success
 *
 * Usage:
 * ```typescript
 * const { upload, status, progress, retry } = useRecordingUpload({
 *   onSuccess: (transcriptId) => console.log('Uploaded:', transcriptId),
 *   onError: (error) => console.error('Upload failed:', error),
 * })
 *
 * // After recording ends
 * const success = await upload(session)
 * if (!success) {
 *   // Show retry button, user can call retry()
 * }
 * ```
 */
export function useRecordingUpload(
  options: UseRecordingUploadOptions = {}
): UseRecordingUploadReturn {
  const { t } = useI18n()
  const { announce } = useAnnouncer()

  // State
  const status = ref<RecordingUploadStatus>('idle')
  const progress = ref<UploadProgress>({ bytesUploaded: 0, totalBytes: 0, percentage: 0 })
  const error = ref<string | null>(null)
  const transcriptId = ref<string | null>(null)

  // Internal state
  let currentSession: RecordingSession | null = null
  let wavBlob: Blob | null = null
  let resumableSessionUri: string | null = null
  let xhr: XMLHttpRequest | null = null
  let retryCount = 0

  // Computed
  const isUploading = computed(() =>
    ['assembling', 'initiating', 'uploading'].includes(status.value)
  )
  const canRetry = computed(() => status.value === 'failed' && currentSession !== null)

  function updateProgress(bytesUploaded: number, totalBytes: number) {
    progress.value = toUploadProgress(bytesUploaded, totalBytes)
    options.onProgress?.(progress.value)
  }

  /**
   * Finalize a successful upload: mark the local session completed, delete it and
   * its chunks from IndexedDB, then supersede the partial streaming transcripts the
   * aborted streaming session(s) left behind (#2828, ADR-033).
   *
   * On an abnormal mid-recording disconnect the whisper service batch-processes the
   * partial audio, so a partial transcript exists server-side; the just-uploaded
   * full WAV is authoritative, so we delete the partial to keep at most one
   * transcript per recording. Deletion is best-effort: a failure is logged and both
   * transcripts are left rather than failing the upload the user just completed.
   */
  async function finalizeUploadedSession(session: RecordingSession): Promise<void> {
    await updateSessionStatus(session.id, 'completed')
    await deleteSession(session.id)
    await deletePartialStreamingTranscripts(session)
  }

  /**
   * Delete the partial streaming transcripts a recovered/failed-over session left
   * behind. Best-effort and non-fatal — see {@link finalizeUploadedSession}. The
   * delete goes through the org-scoped, `recordings.delete`-guarded backend
   * endpoint, so authorization mirrors normal transcript deletion.
   *
   * **A transcript that is still being streamed into is skipped.** Deleting one
   * does not close the whisper session: it keeps buffering, finalizes, and writes
   * a finished transcript into a soft-deleted row — 55 minutes of a production
   * consult, invisible in the UI (#3676). The recorder's registry is the authority
   * on that, not the session read out of IndexedDB, whose transcript list is by
   * construction a *past* attempt's record. Ending the live session instead is not
   * an option here: the socket belongs to a recording somebody is still making, and
   * the recovery upload runs in `App.vue` beside it. The backend refuses the same
   * delete while its session is ACTIVE, so a live transcript is off limits from
   * both ends.
   */
  async function deletePartialStreamingTranscripts(session: RecordingSession): Promise<void> {
    const apiService = initApiService()

    for (const transcriptId of session.streamingTranscriptIds) {
      if (isTranscriptStreaming(transcriptId)) {
        console.warn(
          '[useRecordingUpload] Not superseding a streaming transcript that is still live:',
          transcriptId
        )
        continue
      }

      try {
        await apiService.transcriptions.deleteTranscriptById(transcriptId)
      } catch (e) {
        logUnexpectedFailure(
          '[useRecordingUpload] Failed to delete superseded partial streaming transcript:',
          e
        )
      }
    }
  }

  function reset() {
    status.value = 'idle'
    progress.value = { bytesUploaded: 0, totalBytes: 0, percentage: 0 }
    error.value = null
    transcriptId.value = null
    currentSession = null
    wavBlob = null
    resumableSessionUri = null
    retryCount = 0
    if (xhr) {
      xhr.abort()
      xhr = null
    }
  }

  /**
   * Assembles audio chunks from IndexedDB into WAV format.
   */
  async function assembleAudio(sessionId: string): Promise<Blob> {
    const chunks = await getChunks(sessionId)
    if (chunks.length === 0) {
      throw new UserFacingError(t('audio.noChunksToUpload'))
    }

    // Extract blobs from chunks
    const blobs = chunks.map((c) => c.blob)
    return assembleWav(blobs)
  }

  /**
   * Initiates upload and gets signed URL.
   *
   * Sends exactly one of `appointmentId`/`formSubmissionId` (the backend enforces
   * mutual exclusion) and threads the session's persisted speaker-count hint.
   */
  async function initiateUpload(
    session: RecordingSession,
    filename: string
  ): Promise<{ transcriptId: string; uploadUrl: string }> {
    const apiService = initApiService()
    const link = buildTranscriptionLink(session.appointmentId, session.formSubmissionId ?? '')
    const response = await apiService.whisperUploads.initiateUpload({
      ...link,
      filename,
      contentType: 'audio/wav',
      language: 'nl-NL',
      speakerCount: session.speakerCount,
    })

    return {
      transcriptId: response.data.transcriptId,
      uploadUrl: response.data.uploadUrl,
    }
  }

  /**
   * Say in this screen's words what the transfer reported.
   *
   * `utils/resumableUpload` names the *kind* of failure and holds no copy, so
   * the sentence the user reads is chosen here. A `status` failure keeps the
   * transport's own developer message: the HTTP code is for the log, and
   * `extractApiErrorMessage` falls back to this screen's `audio.uploadFailed`
   * for anything that is not a `UserFacingError`. Anything else — an error from
   * the initiate call, a rejected IndexedDB read — passes through untouched.
   */
  function describeTransferFailure(e: unknown): unknown {
    if (!(e instanceof ResumableUploadError)) return e
    if (e.failure === 'aborted') return new UserFacingError(t('audio.uploadCancelled'))
    if (e.failure === 'network') return new UserFacingError(t('audio.networkError'))
    return e
  }

  /**
   * Queries GCS to find how many bytes have been uploaded.
   */
  async function queryUploadedBytes(sessionUri: string, totalSize: number): Promise<number> {
    try {
      return await queryResumableBytes(sessionUri, totalSize)
    } catch (e) {
      throw describeTransferFailure(e)
    }
  }

  /**
   * Uploads file data starting from the given byte offset.
   *
   * The request is kept so {@link cancel} has something to abort.
   */
  async function uploadFromOffset(
    sessionUri: string,
    file: Blob,
    startByte: number
  ): Promise<void> {
    try {
      await uploadResumableFromOffset({
        sessionUri,
        body: file,
        startByte,
        onProgress: updateProgress,
        onRequestCreated: (request) => {
          xhr = request
        },
      })
    } catch (e) {
      throw describeTransferFailure(e)
    }
  }

  /**
   * Retries upload with exponential backoff.
   */
  async function retryUpload(): Promise<void> {
    if (!wavBlob || !resumableSessionUri) {
      throw new UserFacingError(t('audio.cannotRetryMissingData'))
    }

    retryCount++
    if (retryCount > DEFAULT_MAX_RETRIES) {
      throw new UserFacingError(
        t('audio.uploadFailedAfterRetries', { count: DEFAULT_MAX_RETRIES })
      )
    }

    // Exponential backoff
    const delay = RETRY_DELAY_MS * Math.pow(2, retryCount - 1)
    await new Promise((resolve) => setTimeout(resolve, delay))

    // Query current upload status
    const uploadedBytes = await queryUploadedBytes(resumableSessionUri, wavBlob.size)
    updateProgress(uploadedBytes, wavBlob.size)

    // Resume from where we left off
    await uploadFromOffset(resumableSessionUri, wavBlob, uploadedBytes)
  }

  /**
   * Uploads a completed recording session.
   *
   * The session must be owned by exactly one of an appointment or a form
   * submission; the persisted `speakerCount` hint travels with the session.
   *
   * @param session - The recording session to upload
   * @returns true if upload succeeded, false if failed
   */
  async function upload(session: RecordingSession): Promise<boolean> {
    if (!session.appointmentId && !session.formSubmissionId) {
      error.value = t('audio.noAppointmentOrForm')
      status.value = 'failed'
      options.onError?.(error.value)
      return false
    }

    reset()
    currentSession = session

    try {
      // Step 1: Assemble audio chunks into WAV
      status.value = 'assembling'
      announce('Opname wordt voorbereid voor upload')
      wavBlob = await assembleAudio(session.id)

      // Step 2: Initiate upload and get signed URL
      status.value = 'initiating'
      const filename = generateRecordingFilename(session.startedAt)
      const initResult = await initiateUpload(session, filename)
      transcriptId.value = initResult.transcriptId
      resumableSessionUri = initResult.uploadUrl

      // Step 3: Upload to GCS
      status.value = 'uploading'
      announce('Opname wordt geüpload')
      updateProgress(0, wavBlob.size)
      await uploadFromOffset(resumableSessionUri, wavBlob, 0)

      // Step 4: Mark session completed, clean up, supersede any partial transcript
      await finalizeUploadedSession(session)

      status.value = 'completed'
      announce('Opname succesvol geüpload')
      options.onSuccess?.(transcriptId.value)
      return true
    } catch (e) {
      const errorMessage = extractApiErrorMessage(e, t('audio.uploadFailed'))

      // Try automatic retry if we have a resumable session
      if (resumableSessionUri && retryCount < DEFAULT_MAX_RETRIES) {
        try {
          status.value = 'uploading'
          await retryUpload()

          // Success after retry
          await finalizeUploadedSession(session)

          status.value = 'completed'
          options.onSuccess?.(transcriptId.value!)
          return true
        } catch (retryError) {
          error.value =
            extractApiErrorMessage(retryError, t('audio.uploadFailed'))
        }
      } else {
        error.value = errorMessage
      }

      // Mark session as failed but keep it for manual retry
      try {
        await updateSessionStatus(session.id, 'failed')
      } catch {
        // Ignore - session update failure shouldn't mask the original error
      }

      status.value = 'failed'
      announce(error.value, 'assertive')
      options.onError?.(error.value)
      return false
    }
  }

  /**
   * Retries a failed upload.
   *
   * @returns true if retry succeeded, false if failed
   */
  async function retry(): Promise<boolean> {
    if (!currentSession) {
      error.value = t('audio.noSessionToRetry')
      return false
    }

    // If we still have the WAV blob and resumable session, continue from there
    if (wavBlob && resumableSessionUri) {
      try {
        status.value = 'uploading'
        retryCount = 0 // Reset for manual retry

        const uploadedBytes = await queryUploadedBytes(resumableSessionUri, wavBlob.size)
        updateProgress(uploadedBytes, wavBlob.size)

        if (uploadedBytes >= wavBlob.size) {
          // Upload was already complete
          await finalizeUploadedSession(currentSession)

          status.value = 'completed'
          options.onSuccess?.(transcriptId.value!)
          return true
        }

        await uploadFromOffset(resumableSessionUri, wavBlob, uploadedBytes)

        await finalizeUploadedSession(currentSession)

        status.value = 'completed'
        options.onSuccess?.(transcriptId.value!)
        return true
      } catch (e) {
        error.value = extractApiErrorMessage(e, t('audio.retryFailed'))
        status.value = 'failed'
        options.onError?.(error.value)
        return false
      }
    }

    // Otherwise, start fresh from the session
    const freshSession = await getSession(currentSession.id)
    if (!freshSession) {
      error.value = t('audio.sessionNotFound')
      status.value = 'failed'
      return false
    }

    return upload(freshSession)
  }

  /**
   * Cancels an in-progress upload.
   */
  function cancel() {
    if (xhr) {
      xhr.abort()
      xhr = null
    }
    status.value = 'idle'
  }

  return {
    // State
    status,
    progress,
    error,
    transcriptId,

    // Computed
    isUploading,
    canRetry,

    // Actions
    upload,
    retry,
    cancel,
    reset,
  }
}
